import { Avatar, Box, Dialog, IconButton, Typography } from '@mui/material'
import { CustomLoadingOverlay, sortColDefs } from 'components/common/AgGridUtils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import AddEditReportGroupsModal from './AddEditReportGroupsModal'
import { AgGridReact } from 'ag-grid-react'
import CloseIcon from '@mui/icons-material/Close'
import ConfirmDialog from 'components/common/ConfirmDialog'
import ReportGroupRibbon from './ReportGroupsRibbon'
import ResetFiltersButton from 'components/common/ResetFiltersButton'
import SearchBarWithRecent from 'components/common/SearchBarWithRecent'
import StatusSnackbar from 'components/common/StatusSnackbar'
import { addItemToLS } from 'utils/localStorage'
import { appColors } from 'utils'
import useInnovaAuth from 'components/common/hooks/useInnovaAuth'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import useObjectsOfInterest from 'components/common/hooks/useObjectsOfInterest'
import useOrgIcons from 'components/common/hooks/useOrgIcons'
import useReportGroups from 'components/common/hooks/useReportGroups'

const LS_KEY = 'reportGroupsModal'

const REPORT_TYPES = [{ label: 'RIG KPI SUMMARY', value: 'RIG KPI SUMMARY' }]

const ReportGroupsModal = ({ organization, onCancel }) => {
  const gridApi = useRef(null)
  const _isMounted = useRef(false)

  const [searchText, setSearchText] = useState('')
  const [status, setStatus] = useState({ show: false, severity: 'info', message: '' })
  const [confirm, setConfirm] = useState({ show: false, title: '' })
  const [filterApplied, setFilterApplied] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [groupModal, setGroupModal] = useState({
    open: false,
    mode: 'add', // 'add' | 'edit'
    data: null,
  })

  const { dropDowns } = useObjectsOfInterest()
  const { getCompanyIcon } = useOrgIcons()
  const { userRole } = useInnovaAuth()
  const { getAgGridTheme, searchBarStyle, getActionBarColor, getBackColor, getWindowBarColor } = useInnovaTheme()

  const {
    reportGroups,
    isLoading,
    isAdding,
    isDeleting,
    isUpdating,
    isTesting,
    fetchReportGroups,
    addReportGroup,
    updateReportGroup,
    deleteReportGroup,
    testReportGroup,
  } = useReportGroups()

  useEffect(() => {
    _isMounted.current = true
    fetchReportGroups(organization)

    return () => {
      gridApi.current = null
      _isMounted.current = false
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const showResult = (res) => {
    setStatus({
      show: true,
      severity: res.success ? 'success' : 'error',
      message: res.message,
    })
  }

  const autoSizeColumns = useCallback(() => {
    const api = gridApi.current
    if (!api || api.isDestroyed()) return

    api.autoSizeAllColumns()
  }, [])

  const leftAlignCell = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'start',
      overflow: 'hidden',
    }),
    [],
  )

  const defaultColDef = useMemo(() => {
    return {
      resizable: true,
      sortable: true,
      editable: false,
      autoHeight: true,
      cellStyle: leftAlignCell,
      headerClass: 'header-no-padding',
      filter: 'agSetColumnFilter',
      filterParams: {
        excelMode: 'windows',
      },
    }
  }, [leftAlignCell])

  const onFirstDataRendered = (params) => {
    if (gridApi.current) {
      gridApi.current.onFilterChanged()
    }

    autoSizeColumns()
  }

  const saveColumnState = useCallback(() => {
    const api = gridApi.current
    if (!api || api.isDestroyed()) return

    const colLayout = api.getColumnState()
    if (colLayout) {
      addItemToLS(LS_KEY, 'colLayout', colLayout)
    }
  }, [])

  const gridOptions = useMemo(
    () => ({
      rowSelection: {
        mode: 'singleRow',
        enableClickSelection: true,
        checkboxes: false,
      },
      onDragStopped: saveColumnState,
      onColumnVisible: saveColumnState,
      onExpandOrCollapseAll: autoSizeColumns,
      loadingOverlayComponent: CustomLoadingOverlay,
    }),
    [saveColumnState, autoSizeColumns],
  )

  const getContextMenuItems = (params) => {
    return [
      {
        name: 'Reset columns',
        disabled: false,
        action: () => {
          gridApi.current.resetColumnState()
          addItemToLS(LS_KEY, 'colLayout', null)
        },
        icon: '<span class="iconify" data-icon="carbon:reset" data-width="20" style="color:#4BB2F9"></span>',
        cssClasses: ['leftAlign'],
      },
      'copy',
    ]
  }

  const onFilterRemoved = () => {
    setSearchText('')
  }

  let columnDefs = useMemo(
    () => [
      {
        field: 'icon',
        colId: 'icon',
        headerName: '',
        width: 80,
        resizable: false,
        filter: null,
        sortable: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        headerComponent: ResetFiltersButton,
        headerComponentParams: {
          filterApplied: filterApplied,
          gridApi: gridApi,
          onFilterRemoved: onFilterRemoved,
        },
        pinned: 'left',
        lockPosition: 'left',
        cellRenderer: (params) => {
          if (typeof params?.data?.organization !== 'string') return null

          return (
            <Box
              sx={{
                margin: 'auto',
                width: 35,
                height: 35,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'lightgrey',
                borderRadius: '50%',
              }}>
              <Avatar
                alt={params?.data?.organization}
                sx={{ width: '30px', height: '30px', marginLeft: '1px' }}
                src={getCompanyIcon(params?.data?.organization)}
              />
            </Box>
          )
        },
      },
      {
        field: 'groupName',
        colId: 'groupName',
        headerName: 'Group Name',
        cellStyle: leftAlignCell,
      },
      {
        field: 'organization',
        colId: 'organization',
        headerName: 'Organization',
        cellStyle: leftAlignCell,
      },
      {
        field: 'reportGroupDetails.distributionList',
        colId: 'reportGroupDetails.distributionList',
        headerName: 'Recipients',
        minWidth: 200,
        maxWidth: 600,
        autoHeight: true,
        cellStyle: {
          whiteSpace: 'normal',
          lineHeight: '1.4em',
          padding: '6px',
        },
        cellRenderer: (params) => {
          if (!params.value) return ''
          if (!Array.isArray(params.value)) return ''

          return (
            <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
              {params.value?.map((row, index) => (
                <div key={index}>{row}</div>
              ))}
            </div>
          )
        },
      },
      {
        field: 'reportGroupDetails.rigs',
        colId: 'reportGroupDetails.rigs',
        headerName: 'Rigs',
        minWidth: 200,
        maxWidth: 600,
        autoHeight: true,
        cellStyle: {
          whiteSpace: 'normal',
          lineHeight: '1.4em',
          padding: '6px',
        },
        cellRenderer: (params) => {
          if (!params.value) return ''
          if (!Array.isArray(params.value)) return ''

          return (
            <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
              {params.value?.map((row, index) => (
                <div key={index}>{row}</div>
              ))}
            </div>
          )
        },
      },
      {
        field: 'reportGroupDetails.schedule',
        headerName: 'Schedule',
        minWidth: 250,
        cellStyle: leftAlignCell,
        valueGetter: (params) => params.data?.reportGroupDetails?.schedule,
        cellRenderer: ({ value }) => {
          if (!value) return ''

          if (!value.enabled) return 'Disabled'

          switch (value.type) {
            case 'Daily':
              return `Daily at ${value.time}`

            case 'Weekly':
              return `Every ${value.days?.join(', ')} at ${value.time}`

            case 'Monthly':
              return `Monthly on day ${value.dayOfMonth} at ${value.time}`

            default:
              return ''
          }
        },
      },
    ],
    [getCompanyIcon, filterApplied, leftAlignCell],
  )

  const onFilterChanged = (params) => {
    if (!gridApi.current) return
    if (!_isMounted.current) return

    let text = gridApi.current.getGridOption('quickFilterText')
    setFilterApplied(Object.keys(gridApi.current.getFilterModel()).length > 0 || (text && text.length > 0))
  }

  const clearSelectedGroup = () => {
    if (gridApi.current && !gridApi.current.isDestroyed()) {
      gridApi.current.deselectAll()
    }

    setSelectedGroup(null)
  }

  const onSelectionChanged = useCallback((event) => {
    const nodes = event.api.getSelectedNodes()

    if (!nodes?.length) {
      setSelectedGroup(null)
      return
    }

    const node = nodes[0]

    setSelectedGroup(node.group ? null : (node.data ?? null))
  }, [])

  const handleUpdateGroup = async (form) => {
    const res = await updateReportGroup(form)
    showResult(res)

    if (!res.success) return

    await fetchReportGroups(organization)
    clearSelectedGroup()
    setGroupModal({ open: false, mode: 'add', data: null })
  }

  const handleCreateGroup = async (form) => {
    const res = await addReportGroup(form)
    showResult(res)

    if (!res.success) return

    await fetchReportGroups(organization)
    clearSelectedGroup()
    setGroupModal({ open: false, mode: 'add', data: null })
  }

  const onDelete = (group) => {
    if (!group?.uid) return

    setConfirm({
      show: true,
      title: 'Delete Group',
      text: `Are you sure you want to delete ${group.groupName}?`,
      onConfirm: () => handleDeleteGroup(group.uid),
    })
  }

  const handleDeleteGroup = async (uid) => {
    if (!uid || isDeleting) return

    const res = await deleteReportGroup(uid)
    showResult(res)

    if (!res.success) return

    clearSelectedGroup()
    await fetchReportGroups(organization)
  }

  const handleTestGroup = async (group) => {
    if (!group || isTesting) return

    const res = await testReportGroup(group)
    showResult(res)
  }

  return (
    <Dialog
      fullWidth
      maxWidth='xl'
      PaperProps={{
        sx: {
          backgroundColor: getWindowBarColor(),
          height: '80vh',
          width: '80vw',
          maxWidth: '95vw',
        },
      }}
      open={true}
      onClose={() => onCancel()}>
      <Box
        sx={{
          backgroundColor: getActionBarColor(),
          height: `calc(100vh)`,
          width: `calc(100% )`,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          minWidth: 0,
        }}>
        {confirm.show ? (
          <ConfirmDialog
            title={confirm?.title}
            open={confirm?.show}
            setOpen={() => setConfirm({ show: false })}
            onConfirm={confirm.onConfirm}>
            {confirm?.text}
          </ConfirmDialog>
        ) : null}
        <Box
          sx={{
            height: '44px',
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            color: appColors.headerTextColor,
            backgroundColor: getWindowBarColor(),
            borderBottom: '1px solid rgba(255,255,255,0.12)',
          }}>
          <Typography sx={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Report Groups
          </Typography>

          <IconButton onClick={onCancel} size='small' sx={{ ml: 1 }}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
        {groupModal.open && (
          <AddEditReportGroupsModal
            open={groupModal.open}
            mode={groupModal.mode}
            initialData={groupModal.data}
            onClose={() => setGroupModal({ open: false, mode: 'add', data: null })}
            onSubmit={groupModal.mode === 'edit' ? handleUpdateGroup : handleCreateGroup}
            organization={organization}
            showSpinner={isAdding || isUpdating}
            existingGroups={reportGroups}
            rigs={[
              { label: 'All', value: 'All' },
              ...[...(dropDowns?.rigs || [])].sort((a, b) =>
                (a.label ?? '').localeCompare(b.label ?? '', undefined, { sensitivity: 'base' }),
              ),
            ]}
            reportTypes={[{ label: 'All', value: 'All' }, ...(REPORT_TYPES || [{ label: 'All', value: 'All' }])]}
          />
        )}
        <Box sx={{ p: 1 }}>
          <ReportGroupRibbon
            selectedGroup={selectedGroup}
            onRefresh={() => fetchReportGroups(organization)}
            onAddGroup={() => setGroupModal({ open: true, mode: 'add', data: null })}
            onEditGroup={() => setGroupModal({ open: true, mode: 'edit', data: selectedGroup })}
            onDeleteGroup={onDelete}
            isDeleting={isDeleting}
            userRole={userRole}
            onTestGroup={() => handleTestGroup(selectedGroup)}
          />
        </Box>
        <Box
          sx={{
            backgroundColor: getBackColor(),
            padding: '2px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}>
          <SearchBarWithRecent
            value={searchText}
            onChange={(newSearchTerm) => setSearchText(newSearchTerm)}
            onCancelSearch={() => setSearchText('')}
            style={searchBarStyle()}
            recentKey='usersPage'
          />
        </Box>
        <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
          <div className={getAgGridTheme()} style={{ width: '100%', height: '100%' }}>
            <AgGridReact
              loading={isLoading || isDeleting || isAdding || isUpdating || isTesting}
              rowData={reportGroups}
              columnDefs={sortColDefs(columnDefs, LS_KEY)}
              defaultColDef={defaultColDef}
              getRowId={(params) => String(params.data.uid)}
              animateRows={true}
              gridOptions={gridOptions}
              onGridReady={(params) => {
                gridApi.current = params.api
              }}
              onFirstDataRendered={onFirstDataRendered}
              getContextMenuItems={getContextMenuItems}
              headerHeight={30}
              onFilterChanged={onFilterChanged}
              onSelectionChanged={onSelectionChanged}
            />
          </div>
        </Box>
        <StatusSnackbar status={status} setStatus={setStatus} />
      </Box>
    </Dialog>
  )
}

export default ReportGroupsModal
